/**
 * Design tokens — the entire brand surface. Rebranding a Solum deployment is
 * swapping this one object: colours, type, radius, wordmark. Everything the
 * renderer emits references CSS variables derived from here, so a customer's
 * experience is white-labelled without touching a single view.
 */
export interface BrandTokens {
  name: string
  mark: string // short wordmark glyph (emoji or 1–2 chars) — the fallback when `logo` is unset
  /** the brand's real logo as inline SVG (self-coloured so it reads on light AND
   *  dark). Rendered in the nav in place of `mark`; part of the branding token set
   *  so it's swappable per deployment, not hardcoded. Omit ⇒ the `mark` glyph. */
  logo?: string
  colors: {
    brand: string
    brandContrast: string // text on brand
    accent: string
    // light
    bg: string; surface: string; ink: string; muted: string; border: string
    // dark
    bgDark: string; surfaceDark: string; inkDark: string; mutedDark: string; borderDark: string
  }
  font: { sans: string; mono: string }
  radius: string
  shadow: string
  /** the signature multi-stop gradient (Moonbase's `--grad`) for hero CTAs, the
   *  primary button, brand marks + accent bars. Emitted as `--brand-gradient`;
   *  omit ⇒ falls back to the solid brand colour. */
  gradient?: string
  /** the brand's preferred colour scheme on first load. A dark-first identity
   *  (e.g. Moonbase) declares 'dark' so the console opens dark, not OS-following;
   *  omit ⇒ 'system'. The user's toggle still overrides at runtime. */
  defaultMode?: 'light' | 'dark' | 'system'
  /** How the BASE behaves as a material — the "marble" layer (2026-08-03).
   *  Omit and it is DERIVED from the brand's own colours (see `material()`), so
   *  every brand gets the refinement in its own palette rather than Moonbase's.
   *  Override only when a brand's stone is genuinely different. */
  material?: Partial<Material>
  /** the brand's default control rhythm. Omit ⇒ 'default' (36px controls).
   *  The user's own choice overrides it — density is a per-user setting. */
  density?: Density
}

export type Density = 'compact' | 'default' | 'comfortable'

/** The material layer, resolved. Light and dark side by side, as `colors` does. */
export interface Material {
  /** the full `background` shorthand for the base — light pooling inside a solid,
   *  painted over the flat `bg`. Not a texture: veining is the tell of a copy. */
  baseLight: string; baseDark: string
  /** the hairline on an object's top edge — the cut face catching light. On dark
   *  this does the lifting that a drop shadow cannot (shadows vanish on dark). */
  rimLight: string; rimDark: string
  /** elevation ramp: 1 resting card · 2 drawer/popover · 3 modal. There was only
   *  ever one `--shadow`, and `--shadow-lg` was USED in two modules without ever
   *  being defined — so those popovers silently fell back to no shadow at all. */
  e1Light: string; e2Light: string; e3Light: string
  e1Dark: string; e2Dark: string; e3Dark: string
  /** RELATIONSHIP DEPTH — four accents identifying how deep a relation band is
   *  nested (#2019). Depth is encoded in HUE and not in a tint ramp: a lightness
   *  ladder runs out of contrast after ~3 steps, and runs out sooner in dark
   *  where the same ramp has to travel the other way. A spine colour survives
   *  both, which is why `RelationBand` chose hue in the first place.
   *
   *  They live HERE, with the stone, rather than in a console stylesheet,
   *  because that stylesheet hardcoded Moonbase's seafoam/blue/violet/pink — so
   *  a rebranded tenant received Moonbase's accents on their own relationships.
   *  Derived from the brand's own hue by default (see `material()`); a brand
   *  with four house colours overrides them like any other part of the stone.
   *
   *  ONE set for both themes, deliberately. These identify DEPTH, not state, and
   *  a light/dark pair here is the two-hand-tuned-palettes drift the rest of
   *  this layer exists to prevent. Legibility on both grounds is handled by
   *  clamping saturation and lightness into a band that reads on either. */
  relAccents: [string, string, string, string]
}

/** Solum's own identity — sovereign, premium, calm (the Nespresso north star). */
export const SOLUM: BrandTokens = {
  name: 'Solum',
  mark: '◆',
  colors: {
    brand: '#0f766e', // deep teal
    brandContrast: '#ffffff',
    accent: '#14b8a6',
    bg: '#f6f8f8', surface: '#ffffff', ink: '#0b1a1a', muted: '#5b6b6b', border: '#e2e8e8',
    bgDark: '#0a1414', surfaceDark: '#0f1c1c', inkDark: '#e7f0f0', mutedDark: '#93a7a7', borderDark: '#1e3232',
  },
  font: { sans: `system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`, mono: `ui-monospace, "SF Mono", Menlo, "Cascadia Mono", Consolas, monospace` },
  radius: '12px',
  shadow: '0 1px 2px rgba(0,0,0,.04), 0 8px 24px -12px rgba(0,0,0,.12)',
  gradient: 'linear-gradient(135deg,#14b8a6,#0f766e)',
}

/**
 * The registered Moonbase "MB" monogram, as GEOMETRY rather than as markup.
 *
 * Verbatim from the CMS `logo.svg` — the brand's own source of truth, and the
 * asset ROOT ruling 74071 §1 names: *"use the registered CMS `logo.svg` /
 * governed Moonbase mark already identified by the census. Do not draw another
 * monogram or fall back to typed initials."*
 *
 * It is split out because the same mark now has TWO readers that cannot share
 * one representation:
 *
 *   · `MOONBASE.logo` — a BrandTokens field, i.e. deployment DATA. It travels
 *     through `portal.branding()`, `platform|brand` and a file a person drops
 *     into the branding panel, so it is rendered through `BrandLogo` as an
 *     inert `<img src>` and never as live markup (SEC-17 / #374).
 *   · `MoonbaseMark` in `@solum/ui` — the MAKER attribution inside the OnMarble
 *     launcher. That one is a platform constant and must not be read off
 *     `BrandTokens.logo`, or a Coccolare tenant's console would paint the
 *     CUSTOMER's logo as the "by Moonbase" attribution.
 *
 * The geometry lives HERE because `packages/ui` depends on `@solum/experience`
 * and not the reverse. `MB_LOGO` below is composed from these constants rather
 * than restating them (rule 42): one path, two readers, and a correction to the
 * asset reaches both.
 *
 * The gradient id is deliberately NOT part of this. `MB_LOGO`'s literal
 * `id="mbg"` is safe inside a data-URI `<img>`, which is its own document;
 * `MoonbaseMark` renders INLINE and mints its own id from `useId`, because two
 * marks on one page would otherwise both reference the first one's `<defs>`
 * (the defect `MarbleMark.tsx` already warns about).
 */
export const MOONBASE_MONOGRAM = {
  viewBox: '0 0 354 207',
  /** The group transform that brings the exported artboard back to the origin. */
  transform: 'translate(-724.404,-283.826)',
  /** The "MB" letterform, one filled path. */
  path:
    'M942.226,323.268L942.226,283.826L1000.06,283.826C1041.68,283.95 1068.2,298.589 1070.06,336.906C1070.13,357.85 1060.64,371.241 1045.89,380.492C1063.29,388.717 1075.56,403.442 1077.99,430.625C1075.22,469.433 1052.9,486.858 1018.21,490.746L884.55,490.746L934.405,448.926L1009.51,448.926C1022.64,447.681 1030.69,440.579 1032.33,425.542C1031.05,412.056 1023.39,403.116 1009.51,402.349L942.226,402.349L942.226,362.907L1004.74,362.907C1018.29,362.029 1025.22,355.203 1026.25,343.087C1025.67,331.417 1018.61,324.722 1004.74,323.268L942.226,323.268ZM724.404,490.746L724.404,283.826L765.607,283.826L826.081,359.142L887.523,283.826L927.559,283.826L927.559,443.779L884.55,479.845L884.55,353.988L826.081,423.556L767.81,354.583L767.81,490.746L724.404,490.746Z',
  /** userSpaceOnUse gradient placement, so the sweep crosses the whole mark. */
  gradientTransform: 'matrix(353.588,206.92,-206.92,353.588,724.404,283.826)',
  /** The signature Moonbase gradient, in order. */
  stops: [
    { offset: '0', color: '#54F2D2' },
    { offset: '0.24', color: '#42E0C8' },
    { offset: '0.55', color: '#18B7B1' },
    { offset: '1', color: '#4FA9FF' },
  ],
} as const

// The monogram as MARKUP, for `BrandTokens.logo` — composed from the geometry
// above, never retyped beside it. Self-coloured, so it reads on both the light
// bg and the dark ink.
const MB_LOGO = `<svg viewBox="${MOONBASE_MONOGRAM.viewBox}" xmlns="http://www.w3.org/2000/svg" style="fill-rule:evenodd;clip-rule:evenodd"><g transform="${MOONBASE_MONOGRAM.transform}"><path d="${MOONBASE_MONOGRAM.path}" fill="url(#mbg)"/></g><defs><linearGradient id="mbg" x1="0" y1="0" x2="1" y2="0" gradientUnits="userSpaceOnUse" gradientTransform="${MOONBASE_MONOGRAM.gradientTransform}">${MOONBASE_MONOGRAM.stops.map((s) => `<stop offset="${s.offset}" stop-color="${s.color}"/>`).join('')}</linearGradient></defs></svg>`

/** Moonbase's own identity — seafoam on dark ink, the 06/2026 relaunch brand. */
export const MOONBASE: BrandTokens = {
  name: 'Moonbase',
  mark: '◗',
  logo: MB_LOGO,
  colors: {
    brand: '#18B7B1', // seafoam
    brandContrast: '#04221f', // dark ink on seafoam (seafoam is too light for white)
    accent: '#4FA9FF', // the gradient's blue endpoint
    bg: '#F6F9FC', surface: '#ffffff', ink: '#0B1120', muted: '#5a6b7b', border: '#e3eaf1',
    bgDark: '#0B1120', surfaceDark: '#111b30', inkDark: '#eaf1fb', mutedDark: '#8aa0b8', borderDark: '#1e2b45',
  },
  font: { sans: `Manrope, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`, mono: `ui-monospace, "SF Mono", Menlo, "Cascadia Mono", Consolas, monospace` },
  /* 14px -> 5px (#2406). Spec 1 §4 closes the radius vocabulary to 5px slab /
     3px control / 2px inner detail and says "no other radius exists"; the
     reference screenshots are unambiguously sharp, not soft. The MATERIAL role
     ladder in global.css derives all three from this one knob
     (`--mat-r-slab` = radius, control = x0.6, inner = x0.4), so Moonbase now
     resolves to exactly 5 / 3 / 2 while Acme (4px) stays sharper and Coccolare
     (6px) softer. That is the point of keeping the values derived rather than
     writing 5/3/2 literally: the contract §1.1 requires alternate brands to
     keep working, and a literal ladder would have deleted them.
     This is a BRAND value, so it moves every corner Moonbase renders — which is
     why it lands here, in the change where the controls first consume the
     material radius roles, rather than in the additive token commit. */
  radius: '5px',
  shadow: '0 1px 2px rgba(11,17,32,.05), 0 12px 32px -14px rgba(11,17,32,.18)',
  gradient: 'linear-gradient(135deg,#54F2D2 0%,#42E0C8 32%,#18B7B1 64%,#4FA9FF 100%)', // the canonical Moonbase --grad
  // Light-first since 2026-08-03 (Christopher). Marble reads in light — milky
  // stone is light SCATTERING, and the dark theme is the honest translation of
  // that rather than its origin. Was 'dark' ("portal-v2 is dark by design"); the
  // toggle still overrides, and dark is a first-class theme, not a fallback.
  defaultMode: 'light',
}

/** A contrasting brand — the SAME experience, re-skinned, to prove "brand = a token swap". */
export const ACME: BrandTokens = {
  name: 'Acme Manufacturing',
  mark: '▲',
  colors: {
    brand: '#4338ca', // indigo
    brandContrast: '#ffffff',
    accent: '#f59e0b',
    bg: '#f7f7fb', surface: '#ffffff', ink: '#14121f', muted: '#6b6880', border: '#e6e4ef',
    bgDark: '#0d0b16', surfaceDark: '#161326', inkDark: '#eceaf6', mutedDark: '#a29fb8', borderDark: '#272341',
  },
  font: { sans: `Georgia, "Times New Roman", serif`, mono: `ui-monospace, Menlo, "Cascadia Mono", Consolas, monospace` },
  radius: '4px',
  shadow: '0 1px 2px rgba(0,0,0,.05), 0 10px 30px -14px rgba(0,0,0,.18)',
  gradient: 'linear-gradient(135deg,#4338ca,#f59e0b)',
}

/** Coccolare — "A Private Italian Ritual House". Editorial + restrained: warm
 *  parchment, deep forest-green ink, Cormorant Garamond serif; the Il Superiore
 *  premium tier is the dark palette with amber-gold accent. (customer: coccolare) */
export const COCCOLARE: BrandTokens = {
  name: 'Coccolare',
  mark: '❦',
  colors: {
    brand: '#3F4A3C', // deep forest green
    brandContrast: '#F2EFE8', // parchment on forest
    accent: '#B08D57', // amber / olive gold (Il Superiore)
    bg: '#F2EFE8', surface: '#FBFAF4', ink: '#3F4A3C', muted: '#7C7A6C', border: '#E4DECF',
    bgDark: '#1B1A14', surfaceDark: '#24221A', inkDark: '#EDE7D6', mutedDark: '#A99F86', borderDark: '#39352A',
  },
  font: { sans: `"Cormorant Garamond", "Times New Roman", Georgia, serif`, mono: `ui-monospace, Menlo, "Cascadia Mono", Consolas, monospace` },
  radius: '6px',
  shadow: '0 1px 2px rgba(63,74,60,.06), 0 12px 32px -16px rgba(63,74,60,.22)',
  gradient: 'linear-gradient(135deg,#3F4A3C,#6B7A5E,#B08D57)',
  defaultMode: 'light',
}

/* ══════════════════════════════════════════════════════════════════════════
   The material — "Marble" (2026-08-03)

   The platform's commercial name is Marble, so the base is a material and not a
   fill: milky stone, with objects floating on it. What actually reads as marble
   is how light behaves INSIDE it — it enters, scatters and pools. Veining does
   not: a photographic stone texture is the tell of a copy and dates in a year.

   Two rules hold this up, and both are load-bearing at 200 grid rows:

   1. **The base is milky, the objects are OPAQUE.** Translucency belongs to the
      page. Blur behind text is where soft design becomes an accessibility bug,
      and `backdrop-filter` per card is where a long list drops frames. Lift
      comes from a shadow plus a hairline rim — both free.
   2. **Dark is not light, darkened.** "Milky" is light scattering through a
      solid; you cannot scatter white on a dark base without getting grey mud.
      Dark is the same stone lit from the other side: a luminous fog in the
      base, and objects separated by a RIM rather than a drop shadow, because a
      drop shadow on near-black is invisible.

   DERIVED, not hardcoded. The pools are built from the brand's own `brand` and
   `accent` at low alpha, so Coccolare gets parchment marble and Acme indigo
   marble from the same code. Writing Moonbase's seafoam in here would have made
   the refinement a Moonbase skin and quietly broken "brand = a token swap".
   ═════════════════════════════════════════════════════════════════════════ */

/** #rrggbb → `r,g,b`, for building the rgba() pools. Falls back to a neutral so
 *  a brand carrying a named colour or a gradient string can never crash a theme. */
function rgb(hex: string): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return '127,127,127'
  const n = parseInt(m[1]!, 16)
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`
}

/** The four relationship-depth accents, derived from the brand's own hue (#2019).
 *
 *  ROTATION, not a tint ramp — see `Material.relAccents`. The ladder is
 *  `0 / 38 / 76 / 114` degrees: a quarter turn spread over four steps. That
 *  spacing is not arbitrary, it is MEASURED off the set the console shipped
 *  with — Moonbase's seafoam #18B7B1, blue #4FA9FF, violet #9C8BFF and pink
 *  #E58BD0 sit at hues 177 / 211 / 248 / 313, i.e. steps of +34, +37, +65. So a
 *  brand keeps the look the design was tuned against while a rebranded tenant
 *  gets the same relationship in their own hue.
 *
 *  ALL FOUR SIT AT ONE LUMINANCE, and that is the mechanism rather than a
 *  detail. These identify depth by HUE, so holding luminance constant is what
 *  makes the ladder a hue ladder instead of a tint ramp — and it is also the
 *  only way one value can be legible on BOTH grounds, which they must be
 *  because they deliberately do not vary by theme.
 *
 *  The band is arithmetic, not taste. WCAG 2.2 SC 1.4.11 asks 3:1 for a
 *  graphical object that carries meaning, and against the two shipped grounds
 *  that pins relative luminance Y to a narrow window:
 *
 *    vs #ffffff (Y=1.000):  1.05 / (Y + 0.05) >= 3  ->  Y <= 0.300
 *    vs #111b30 (Y=0.0112): (Y + 0.05) / 0.0612 >= 3 ->  Y >= 0.134
 *
 *  `TARGET_Y = 0.20` sits in the middle of [0.134, 0.300] with room either side
 *  for a brand hue whose saturation pulls it about.
 *
 *  This replaced an HSL-lightness clamp, and the correction is worth recording:
 *  HSL lightness is NOT luminance. `hsl(h 60% 57%)` measures anywhere from
 *  Y=0.11 to Y=0.45 depending on hue — yellow is far brighter than blue at the
 *  same L — so a clamp on L let three of four accents fall through the floor on
 *  one ground. The contrast assertions in `rel-accents.test.ts` found that, in
 *  this lane, against my own first implementation.
 *
 *  Saturation is still clamped: a near-grey brand would otherwise yield four
 *  indistinguishable spines however correct their luminance.
 *
 *  A brand whose colour is not an opaque hex (a named colour, a gradient string)
 *  falls back to four neutral steps, spaced INSIDE the same window rather than
 *  across a full grey ramp — visible on both grounds, never a crash, and never
 *  Moonbase's. */
const TARGET_Y = 0.2

function relAccents(brand: string): [string, string, string, string] {
  const parsed = parseHex(brand)
  if (!parsed) return ['#6f6f6f', '#7b7b7b', '#868686', '#8e8e8e']
  const [h, s0] = rgbToHsl(parsed)
  const s = Math.min(0.75, Math.max(0.5, s0))
  return [0, 38, 76, 114].map((d) => atLuminance((h + d) % 360, s, TARGET_Y)) as
    [string, string, string, string]
}

/** The #rrggbb at hue `h` and saturation `s` whose WCAG luminance is closest to
 *  `target`. Luminance rises monotonically with HSL lightness at a fixed hue and
 *  saturation, so a bisection converges — 20 steps is well past 8-bit precision
 *  and costs nothing at module load. */
function atLuminance(h: number, s: number, target: number): string {
  let lo = 0, hi = 1
  for (let i = 0; i < 20; i++) {
    const mid = (lo + hi) / 2
    if (luminance(hslToHex(h, s, mid)) < target) lo = mid
    else hi = mid
  }
  return hslToHex(h, s, (lo + hi) / 2)
}

/** [r,g,b] 0-255 → [h 0-360, s 0-1, l 0-1]. */
function rgbToHsl([r, g, b]: [number, number, number]): [number, number, number] {
  const rn = r / 255, gn = g / 255, bn = b / 255
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  const d = max - min
  if (d === 0) return [0, 0, l]
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  const h = max === rn ? ((gn - bn) / d + (gn < bn ? 6 : 0))
    : max === gn ? (bn - rn) / d + 2
    : (rn - gn) / d + 4
  return [h * 60, s, l]
}

/** [h 0-360, s 0-1, l 0-1] → #rrggbb. */
function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  const seg = Math.floor(h / 60) % 6
  const [r, g, b] = seg === 0 ? [c, x, 0] : seg === 1 ? [x, c, 0] : seg === 2 ? [0, c, x]
    : seg === 3 ? [0, x, c] : seg === 4 ? [x, 0, c] : [c, 0, x]
  return toHex([(r + m) * 255, (g + m) * 255, (b + m) * 255])
}

/* ══════════════════════════════════════════════════════════════════════════
   Contrast — the arithmetic, so a colour decision can be MEASURED

   These three exist because "brand = a token swap, zero component changes" is a
   promise the design system makes to a CUSTOMER, and a customer choosing their
   own brand colour was able to push a conformance floor below the standard
   without anyone noticing. Measured on 2026-08-05 across the four shipped
   brands, the keyboard focus ring (`2px solid var(--brand)` against the page
   behind it) was: MOONBASE light 2.49:1, ACME dark 2.30:1, COCCOLARE dark
   1.71:1 — against a WCAG 2.2 SC 1.4.11 floor of 3:1. Coccolare is a real
   customer and 1.71:1 is very close to not visible at all.

   The remedy is not a better hex value; it is that the ring stops being a raw
   customer token. `focusRing` derives one that CANNOT fall below the floor, and
   `packages/ui/src/theme/focus-ring.test.ts` fails if a future brand breaks it.
   ═════════════════════════════════════════════════════════════════════════ */

/** #rrggbb → [r,g,b] 0-255. Returns null for anything that is not an opaque hex
 *  (a named colour, a gradient string) — callers must decide what that means
 *  rather than silently measuring a fallback grey and calling it a pass. */
function parseHex(hex: string): [number, number, number] | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1]!, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

const toHex = ([r, g, b]: [number, number, number]) =>
  '#' + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')

/** WCAG 2.x relative luminance of an opaque sRGB colour. */
export function luminance(hex: string): number {
  const rgb = parseHex(hex)
  if (!rgb) return 0.5
  const [r, g, b] = rgb.map((v) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** WCAG 2.x contrast ratio, 1..21. Order-independent. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number]
  return (hi + 0.05) / (lo + 0.05)
}

/** WCAG 2.2 SC 1.4.11 Non-text Contrast (AA): user-interface component
 *  boundaries and state indicators need 3:1 against what is adjacent. */
export const NON_TEXT_CONTRAST_FLOOR = 3

/** What `focusRing` actually aims for. The FLOOR is 3:1 and that is what the
 *  tests assert; the derivation targets a little above it because the ring is
 *  not drawn on the flat `--bg` token — `--base` is a marble of very wide,
 *  very low-contrast light pools over it (see `material()`), so the pixel a
 *  browser composites under the outline is near `--bg` but not equal to it.
 *  Deriving exactly to 3.00 produced rings measuring 3.01 against the token and
 *  gave that compositing nowhere to go. The margin is small on purpose: every
 *  step away from 3:1 is a step away from the customer's own colour. */
const FOCUS_RING_TARGET = 3.2

const mix = (a: [number, number, number], b: [number, number, number], t: number): [number, number, number] =>
  [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]

/**
 * The keyboard focus ring for a brand, against the surfaces it will be drawn on.
 *
 * The ring keeps the brand's identity wherever the brand already clears 3:1
 * (SOLUM light is returned unchanged, MOONBASE dark likewise) — this is a
 * conformance floor, not a re-brand. Where it does not, the brand is walked
 * toward black or toward white, whichever reaches the floor with the SMALLER
 * deviation, so the ring still reads as the customer's colour rather than as a
 * generic black outline.
 *
 * `backgrounds` is every surface the ring can land on. `outline-offset: 2px`
 * puts it outside the control, so in practice that is the page `--bg` and the
 * card `--surface`; both must clear the floor, because one page has both.
 *
 * If NO colour on either axis clears the floor against every background — which
 * needs a brand whose bg and surface are at opposite ends of the luminance range
 * — the best available is returned and the token test goes red naming the
 * brand. That is deliberate: a brand nobody can draw a legible ring on is a
 * brand-definition defect, and it should be reported, not silently approximated.
 */
export function focusRing(brand: string, backgrounds: string[]): string {
  const worst = (c: string) => Math.min(...backgrounds.map((b) => contrastRatio(c, b)))
  if (worst(brand) >= FOCUS_RING_TARGET) return brand

  const base = parseHex(brand)
  if (!base) return worst('#000000') >= worst('#ffffff') ? '#000000' : '#ffffff'

  let best = brand
  let bestScore = worst(brand)
  // 2% steps: fine enough that the ring stays recognisably the brand colour,
  // coarse enough to stay a cheap pure function called on every theme apply.
  for (let t = 0.02; t <= 1.0001; t += 0.02) {
    const reached: { hex: string; score: number }[] = []
    for (const target of [[0, 0, 0], [255, 255, 255]] as [number, number, number][]) {
      const hex = toHex(mix(base, target, t))
      const score = worst(hex)
      if (score >= FOCUS_RING_TARGET) reached.push({ hex, score })
      else if (score > bestScore) { best = hex; bestScore = score }
    }
    // Both directions can clear at the same step; take the more legible one so
    // the result does not depend on the order of the array above.
    if (reached.length) return reached.sort((a, b) => b.score - a.score)[0]!.hex
  }
  return best
}

/* ══════════════════════════════════════════════════════════════════════════
   `--brand-text` — the same argument as the focus ring, one floor higher

   `focusRing` fixed the ring because a customer-supplied colour was deciding a
   WCAG 1.4.11 floor. TEXT drawn in `var(--brand)` is the same defect against SC
   1.4.3, and it is far more widespread: **140 text paints** — 133 `color:
   var(--brand)` declarations across `packages/ui`, `apps/console`,
   `apps/portal` and `packages/auth`, PLUS seven that no stylesheet contains:
   six inline `style={{ color: 'var(--brand)' }}` props in console desks and
   one embedded stylesheet in `render.ts`, shipped as a string by
   `renderExperience()`.

   That split is not trivia. The first ratchet walked `.css` only and reported
   ZERO offenders with all seven live (Root, #2062 comment 62107), so the
   counts are now exact and split by kind in `brand-text-contrast.test.ts`.
   The stylesheet figure alone went 186 -> 158 -> 134 -> 133 across three
   separate regex errors, the last of which counted a line of PROSE the sweep
   had rewritten in `Chip.module.css` (restored).

   Rendered, every one of them is text — a runtime sweep of nine console
   routes found zero icon-only nodes among them. Measured against the grounds
   below, the RAW brand fails 4.5:1 in SIX of the eight registered brand x
   mode cells, worst COCCOLARE dark at 1.37:1.

   ── the ground is the whole problem ────────────────────────────────────────
   `focusRing` measures against `--bg` and `--surface`, and for a 2px stroke
   with `outline-offset` that is the truth. Brand text is not: it sits inside
   brand-tinted chips, pills and active tabs, and those tints are built FROM the
   brand, so they move with it. MOONBASE light measures 2.49:1 on white and
   **1.90:1** on the worst tinted ground — clamping against white alone would
   have shipped a token that still failed where the colour is actually used.

   So the ground set is enumerated, not assumed, and it is DERIVED rather than
   restated: `brandTextGrounds` reads the pool alphas back out of `material()`'s
   own output. Move a pool in `material()` and the clamp follows it; there is no
   second copy of those numbers to drift (skill rule 26).

   ── why the target is exactly the floor ────────────────────────────────────
   `FOCUS_RING_TARGET` aims above 3:1 because the ring is drawn on a marble the
   derivation does not enumerate, so it needs blind headroom. This one does
   enumerate it — including a composite of every pool at peak alpha, which no
   single pixel can actually receive because the pools sit at different
   positions. That unreachable bound IS the margin, and it is a margin that
   stays correct if someone later moves the pools together. Putting the headroom
   in the ground set rather than in a number above the floor keeps the
   derivation as close to the customer's colour as conformance allows.
   ═════════════════════════════════════════════════════════════════════════ */

/** WCAG 2.x SC 1.4.3 Contrast (Minimum), AA, normal text. Large text (>=24px,
 *  or >=18.66px at weight >=700) has a 3:1 floor, and a colour that clears 4.5
 *  clears that too — one token rather than two that drift. */
export const TEXT_CONTRAST_FLOOR = 4.5

/** The heaviest `color-mix(… var(--brand) N% …)` that sits UNDER brand text in
 *  the shipped stylesheets. It bounds the ground set, so it is not a free
 *  number: `packages/ui/src/theme/brand-text-contrast.test.ts` re-derives it
 *  from the CSS and fails if a rule ever puts brand text on a heavier tint than
 *  the clamp was solved for. */
export const MAX_BRAND_TINT_UNDER_TEXT = 0.16

/** `src` composited over `dst` at alpha `a` — what the browser paints for
 *  `color-mix(in srgb, src (a*100)%, dst)` and for an `rgba()` gradient stop. */
function composite(src: string, dst: string, a: number): string {
  const s = parseHex(src), d = parseHex(dst)
  if (!s || !d) {
    throw new Error(`Cannot composite an unmeasurable colour: ${src} over ${dst}`)
  }
  return toHex([0, 1, 2].map((i) => a * s[i]! + (1 - a) * d[i]!) as [number, number, number])
}

interface MeasurableMaterialBase {
  flat: string
  layers: { hex: string; a: number }[]
}

/** Split a CSS background at top-level commas. A comma inside rgba() or a
 * gradient belongs to that layer, so String.split(',') cannot describe the
 * material that the browser paints. */
function splitBackgroundLayers(value: string): string[] {
  const layers: string[] = []
  let depth = 0
  let start = 0
  for (let i = 0; i < value.length; i++) {
    if (value[i] === '(') depth++
    else if (value[i] === ')') depth--
    else if (value[i] === ',' && depth === 0) {
      layers.push(value.slice(start, i).trim())
      start = i + 1
    }
    if (depth < 0) return []
  }
  if (depth !== 0) return []
  layers.push(value.slice(start).trim())
  return layers.filter(Boolean)
}

/** The subset of CSS backgrounds whose worst-case painted colours this module
 * can actually enumerate. Arbitrary CSS remains valid for `material()` itself,
 * but it cannot cross the theme-emission boundary while it is a text ground:
 * accepting a named colour, another gradient grammar, or a missing flat would
 * make the contrast result a claim about pixels we never measured. */
function measurableMaterialBase(value: string, label: string): MeasurableMaterialBase {
  const parts = splitBackgroundLayers(value.trim())
  const flat = parts.at(-1)
  if (!flat || !parseHex(flat)) {
    throw new Error(`${label} is not measurable: the material must end in an opaque #rrggbb ground`)
  }
  if (parts.length === 1) return { flat, layers: [] }

  const layers: { hex: string; a: number }[] = []
  const radial = /^radial-gradient\([^,]+,\s*rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(0?\.\d+|1(?:\.0+)?)\s*\)\s*0%\s*,\s*rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*0(?:\.0+)?\s*\)\s*\d+(?:\.\d+)?%\s*\)$/i
  for (const part of parts.slice(0, -1)) {
    const match = radial.exec(part)
    if (!match) {
      throw new Error(`${label} is not measurable: unsupported material layer ${part}`)
    }
    const first = [+match[1]!, +match[2]!, +match[3]!] as [number, number, number]
    const fade = [+match[5]!, +match[6]!, +match[7]!] as [number, number, number]
    const alpha = +match[4]!
    if ([...first, ...fade].some((channel) => channel < 0 || channel > 255)
      || first.some((channel, i) => channel !== fade[i]) || alpha <= 0 || alpha > 1) {
      throw new Error(`${label} is not measurable: invalid material colour stop ${part}`)
    }
    layers.push({ hex: toHex(first), a: alpha })
  }
  return { flat, layers }
}

/**
 * Every ground a `var(--brand-text)` run can be painted on, for one mode.
 *
 * Three families, and the third is the one that matters:
 *
 *  1. the opaque panels — `--surface`, and the flat colour under the marble;
 *  2. the marble's own pools, read back out of `material()` so the alphas have
 *     exactly one home. Plus `base·pools` — all of them composited at peak,
 *     which is not reachable at a single pixel and is included as a BOUND;
 *  3. the brand tint at `MAX_BRAND_TINT_UNDER_TEXT` over each of the above. This
 *     family is why the token exists: the tint is built from the brand, so a
 *     brand that is hard to read gets a ground that moves toward it.
 *
 * Returned labelled rather than as a bare array so a failure can name the
 * ground it failed on instead of an index.
 */
export function brandTextGrounds(t: BrandTokens, mode: 'light' | 'dark'): Record<string, string> {
  const dark = mode === 'dark'
  const surface = dark ? t.colors.surfaceDark : t.colors.surface
  const base = dark ? material(t).baseDark : material(t).baseLight
  if (!parseHex(surface)) {
    throw new Error(`${t.name} ${mode} surface is not measurable: expected an opaque #rrggbb colour`)
  }

  // `material()` emits `…rgba(R,G,B,.AA) 0%, rgba(R,G,B,0) NN%)…,<flat>`.
  // Parse that exact measurable grammar. There is deliberately no `colors.bg`
  // fallback: substituting a different ground would make an unsupported material
  // look conformant by measuring pixels the browser did not paint.
  const { layers, flat } = measurableMaterialBase(base, `${t.name} ${mode} base`)

  const opaque: Record<string, string> = { surface, 'base·flat': flat }
  // CSS paints a comma-separated background list FIRST-on-top, so the pool that
  // reaches the eye last is the one written first. Composite in reverse.
  let stacked = flat
  for (const l of [...layers].reverse()) stacked = composite(l.hex, stacked, l.a)
  const pools: Record<string, string> = { 'base·pools': stacked }
  layers.forEach((l, i) => { pools[`base·pool${i + 1}`] = composite(l.hex, flat, l.a) })

  const out: Record<string, string> = { ...opaque, ...pools }
  const pct = Math.round(MAX_BRAND_TINT_UNDER_TEXT * 100)
  for (const [k, v] of Object.entries({ ...opaque, ...pools })) {
    out[`tint${pct}·${k}`] = composite(t.colors.brand, v, MAX_BRAND_TINT_UNDER_TEXT)
  }
  return out
}

/**
 * OKLab coordinates of an opaque sRGB colour (Björn Ottosson, 2020).
 *
 * OKLab rather than raw RGB because the question `brandText` asks — "which of
 * these two still looks like the customer's colour?" — is a perceptual one, and
 * Euclidean distance in sRGB answers a different question badly: it treats a
 * step in the greens as the same size as a step in the blues. It is also the
 * space the stylesheets already mix in (`color-mix(in oklab, …)` appears
 * throughout `packages/ui`), so the derivation and the CSS agree about what
 * "halfway" means.
 *
 * NOTE the 0.04045 transfer-function threshold here versus 0.03928 in
 * `luminance` above. That is deliberate, not a typo: WCAG 2.x specifies 0.03928
 * for its relative-luminance formula and sRGB specifies 0.04045 for linearising.
 * They differ in the 4th decimal and each is used where its own spec says.
 */
function oklab(hex: string): [number, number, number] {
  const parsed = parseHex(hex)
  if (!parsed) return [0, 0, 0]
  const [r, g, b] = parsed.map((v) => {
    const c = v / 255
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  }) as [number, number, number]
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  return [
    0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s,
  ]
}

/**
 * Whether a colour can take part in a contrast decision at all.
 *
 * `luminance()` answers 0.5 for anything it cannot parse — a named colour, an
 * `rgb()` string, a gradient — which is a plausible number and a false one. Any
 * gate that reads a ratio without asking this first is measuring a mid-grey and
 * reporting it as the customer's colour. Exported so a caller, a test or a
 * validating boundary can refuse the input instead of discovering it later.
 */
export function isMeasurableColor(colour: string): boolean {
  return parseHex(colour) !== null
}

/** How far apart two colours LOOK — Euclidean distance in OKLab. Exported so a
 *  test can assert the closest-colour rule rather than reimplement it. */
export function perceptualDistance(a: string, b: string): number {
  const [l1, a1, b1] = oklab(a)
  const [l2, a2, b2] = oklab(b)
  return Math.hypot(l1 - l2, a1 - a2, b1 - b2)
}

/**
 * The colour brand-coloured TEXT is drawn in, against the grounds it lands on.
 *
 * Same shape as `focusRing`, one floor higher, and with the tie-break the ring
 * still gets wrong. The brand is returned UNCHANGED wherever it already clears
 * 4.5:1 (ACME light and COCCOLARE light come back untouched). Where it does not,
 * each axis — toward black, toward white — is walked in 2% steps to the FIRST
 * colour that clears the floor, and of those two the one PERCEPTUALLY CLOSEST to
 * the brand wins, measured in OKLab.
 *
 * That last clause is the white-label promise made literal, and it is not what
 * "whichever cleared first, higher contrast breaking the tie" does. A step count
 * is an interpolation fraction, not a distance: for a light brand, 30% toward
 * white barely moves and 30% toward black is most of the way to unrecognisable,
 * so comparing the two by step or by contrast score prefers whichever direction
 * the arithmetic happened to favour rather than the one that still looks like
 * the customer. `focusRing()` has the same tie-break and is deliberately NOT
 * changed here: its values ship today and re-deriving them is its own change.
 *
 * This is not free, and the cost should be stated rather than discovered:
 * MOONBASE light seafoam `#18B7B1` cannot be AA text on any of these grounds
 * (1.90:1 at worst), so it resolves to a deep teal. That is a visible change to
 * all 140 governed text paints. The alternative is text nobody with low vision
 * can read.
 *
 * If nothing on either axis clears the floor against every ground — which needs
 * a ground set spanning most of the luminance range, i.e. a broken brand — the
 * theme is refused. Returning the "best" failing colour would turn a known
 * conformance failure into a token the rest of the product trusts. A brand
 * nobody can write legible text in is a brand-definition defect and should be
 * reported, not approximated.
 */
export function brandText(brand: string, grounds: string[]): string {
  if (grounds.length === 0 || grounds.some((ground) => !parseHex(ground))) {
    throw new Error('brandText requires at least one measurable opaque #rrggbb ground')
  }
  const worst = (c: string) => Math.min(...grounds.map((g) => contrastRatio(c, g)))

  // MEASURABILITY IS CHECKED FIRST, and the order is the whole point.
  //
  // `luminance()` substitutes 0.5 for anything it cannot parse, so `worst()` on
  // an unsupported colour returns a number that describes a mid-grey rather than
  // the colour. Asking `worst(brand) >= FLOOR` before knowing the brand is
  // measurable therefore hands back the raw value on the strength of a
  // measurement that was never taken: `brand: 'blue'` on MOONBASE dark modelled
  // 6.32:1 and actually renders about 2.00:1 on `#111b30`. Portal branding
  // accepts arbitrary JSON and `BrandColors` fields are plain strings, so no
  // boundary refuses that today (Root, #2062 comment 62107).
  //
  // So an unsupported colour FAILS CLOSED: it never reaches the early return or
  // the page. Picking black or white would still be silently accepting an invalid
  // theme and would let its raw brand leak through every non-text use of
  // `--brand`. The theme/branding boundary must refuse the document instead.
  const base = parseHex(brand)
  if (!base) throw new Error('brandText requires a measurable opaque #rrggbb brand colour')

  if (worst(brand) >= TEXT_CONTRAST_FLOOR) return brand

  // Walk each axis to ITS OWN first clearing colour, then choose between the two
  // by distance. Walking both together and taking whichever cleared first — or
  // whichever scored higher when they cleared at the same step — is the bug this
  // replaced: the loop index `t` is an interpolation fraction, and the same
  // fraction toward black and toward white are not the same distance from the
  // brand. For a light brand, 30% toward white is a small change and 30% toward
  // black is a large one, so a rule that compares them by `t` or by contrast
  // silently prefers whichever direction the arithmetic happened to favour.
  let best = brand
  let bestScore = worst(brand)
  const candidates: string[] = []
  for (const target of [[0, 0, 0], [255, 255, 255]] as [number, number, number][]) {
    for (let t = 0.02; t <= 1.0001; t += 0.02) {
      const hex = toHex(mix(base, target, t))
      const score = worst(hex)
      if (score >= TEXT_CONTRAST_FLOOR) { candidates.push(hex); break }
      if (score > bestScore) { best = hex; bestScore = score }
    }
  }
  if (!candidates.length) {
    throw new Error(
      `brandText cannot meet ${TEXT_CONTRAST_FLOOR}:1 across every supplied ground; best measured ${bestScore.toFixed(2)}:1 (${best})`,
    )
  }
  // Ties broken on the hex so the result is a pure function of its inputs and
  // not of array order.
  return candidates.sort((a, b) =>
    (perceptualDistance(brand, a) - perceptualDistance(brand, b)) || a.localeCompare(b))[0]!
}

/* ══════════════════════════════════════════════════════════════════════════
   `--muted` — the same argument again, on the token that carries most of the
   product's secondary text

   #2129, ruled by Root as a GLOBAL INVARIANT on a universal semantic ink role:
   `--muted` keeps its name and its meaning, and gets the closest value that
   clears the applicable floor over every governed ground it can occupy.

   ── why it is not `--brand-text`'s problem twice ───────────────────────────
   `--brand-text` is a NEW token for a colour that was never meant to be text.
   `--muted` has always been text — remeasured 2026-09-15 across every `.css`,
   `.ts` and `.tsx` file in the repo, 755 of its 805 occurrences paint `color`
   — so there is nothing to introduce and nothing to migrate. The value moves;
   every call site stays exactly as it is. That is the whole difference, and it
   is why this is a token derivation rather than a sweep.

   ── the ground, again, and why #2128 did not land ─────────────────────────
   Measured 2026-09-08 and remeasured 2026-09-15, the RAW `--muted` fails 4.5:1
   in ALL EIGHT registered brand x mode cells, 2.61-3.86 against a 4.5 floor.
   (Six of eight is `--brand-text`'s premise, not this one; the two were
   transposed here, and `muted-text-contrast.test.ts` pins the eight.) #2128
   proposed a clamp against flat grounds and a 14% ink tint. The 14% bound
   survives measurement; what that proposal missed is WHICH ground binds: for
   MOONBASE dark it is the quiet tint over the MARBLE POOL at 2.73:1 while the
   same tint over the flat base is a passing 4.90:1, and COCCOLARE light is the
   same shape at 2.61:1 against 3.02:1. Solving against flat grounds alone
   leaves every pool ground failing, which is the same mistake `--brand-text`
   made against white before its ground set was enumerated.

   The observed nested InsightCard/SegmentedControl ground composites to about
   13%, so one conservative 14% layer covers it. Modelling the two source tints
   as two sequential 14% layers would invent a darker ground the browser never
   paints and makes five dark cells mathematically unsatisfiable.
   ═════════════════════════════════════════════════════════════════════════ */

/** Conservative bound for a quiet tint under muted text: 14%, measured from
 *  the rendered nested consumer (about 13%). The tree also contains 22% quiet
 *  backgrounds, but no muted glyph occupies those grounds; treating every
 *  background as reachable made the problem unsatisfiable rather than safer. */
export const MAX_QUIET_TINT = 0.14

/** Number of conservative bound layers. One 14% layer represents the measured
 *  composite of the two nested source tints; stacking the bound twice would
 *  double-count that composition. */
export const QUIET_TINT_STACK = 1

/**
 * Every ground `--muted` text can be painted on, for one mode — or a refusal.
 *
 * FAIL-CLOSED, and the reason is the hole an independent audit found in
 * `brandText` (Root, #2062 comment 62838): validating only the FOREGROUND lets
 * an unsupported ground flow through `luminance()`'s 0.5 fallback and approve a
 * real sub-4.5 pair. A brand may carry an arbitrary `material` override and
 * arbitrary `colors`, both plain strings, so "the ground is a hex" is an
 * assumption and not a fact.
 *
 * So this returns the grounds it could MEASURE and, separately, every input it
 * could not. A caller that ignores `unmeasurable` is choosing to approve a
 * colour nobody evaluated, and the tests here refuse to.
 */
export function mutedTextGrounds(t: BrandTokens, mode: 'light' | 'dark'): {
  grounds: Record<string, string>
  unmeasurable: { key: string; value: string }[]
} {
  const dark = mode === 'dark'
  const unmeasurable: { key: string; value: string }[] = []
  const measured = (key: string, value: string): string | null => {
    if (isMeasurableColor(value)) return value
    unmeasurable.push({ key, value })
    return null
  }

  const surface = measured('surface', dark ? t.colors.surfaceDark : t.colors.surface)
  const ink = measured('ink', dark ? t.colors.inkDark : t.colors.ink)
  const flatToken = dark ? t.colors.bgDark : t.colors.bg
  const base = dark ? material(t).baseDark : material(t).baseLight

  // The marble's pools, read back out of `material()`'s own emitted gradients —
  // one home for those alphas, so moving a pool moves the clamp (rule 26).
  const layers = [...base.matchAll(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*(0?\.\d+|1)\)/g)]
    .map((m) => ({ hex: toHex([+m[1]!, +m[2]!, +m[3]!]), a: parseFloat(m[4]!) }))
    .filter((l) => l.a > 0)
  // A material override can be anything a brand author writes, including a
  // gradient with no parseable stops or a bare colour name. If the emitted base
  // does not end in an opaque hex, it is NOT silently replaced with `colors.bg`
  // — that substitution is exactly how a fictional ground gets approved.
  const flatFromBase = /(#[0-9a-f]{6})\s*$/i.exec(base.trim())?.[1] ?? null
  const flat = flatFromBase ?? measured('base.flat', flatToken)

  const opaque: Record<string, string> = {}
  if (surface) opaque['surface'] = surface
  if (flat) opaque['base·flat'] = flat

  const pools: Record<string, string> = {}
  if (flat) {
    let stacked = flat
    for (const l of [...layers].reverse()) stacked = composite(l.hex, stacked, l.a)
    pools['base·pools'] = stacked
    layers.forEach((l, i) => { pools[`base·pool${i + 1}`] = composite(l.hex, flat, l.a) })
  }
  if (base.includes('gradient') && layers.length === 0) {
    // A material that IS a gradient but exposes no rgba stops cannot be reduced
    // to grounds. Reported, never guessed — the same refusal the browser decider
    // makes when it meets a `background-image`.
    unmeasurable.push({ key: 'material.base', value: base.slice(0, 60) })
  }

  const grounds: Record<string, string> = { ...opaque, ...pools }

  // The quiet-tint family. The 14% bound already represents the measured
  // composite of the nested InsightCard/SegmentedControl case, so one layer is
  // conservative and a second would double-count the same ground.
  if (ink) {
    const pct = Math.round(MAX_QUIET_TINT * 100)
    for (const [k, v] of Object.entries({ ...opaque, ...pools })) {
      let g = v
      for (let depth = 1; depth <= QUIET_TINT_STACK; depth++) {
        g = composite(ink, g, MAX_QUIET_TINT)
        grounds[`ink${pct}x${depth}·${k}`] = g
      }
    }
  }

  return { grounds, unmeasurable }
}

/**
 * The value `--muted` resolves to: the closest colour to the author's own muted
 * ink that clears the text floor on every ground it can occupy.
 *
 * Identical in shape to `brandText`, and deliberately so — one derivation
 * argued once. The brand is returned UNCHANGED where it already clears, so a
 * palette that is already conformant is byte-identical and no consumer moves.
 *
 * An unmeasurable muted value fails CLOSED for the same reason `brandText` now
 * does: `luminance()` answers 0.5 for what it cannot parse, so asking "does it
 * clear?" before knowing it is a colour approves a measurement of mid-grey.
 */
export function mutedText(muted: string, grounds: string[]): string {
  const worst = (c: string) => Math.min(...grounds.map((g) => contrastRatio(c, g)))

  const base = parseHex(muted)
  if (!base) return worst('#000000') >= worst('#ffffff') ? '#000000' : '#ffffff'
  if (worst(muted) >= TEXT_CONTRAST_FLOOR) return muted

  let best = muted
  let bestScore = worst(muted)
  const candidates: string[] = []
  for (const target of [[0, 0, 0], [255, 255, 255]] as [number, number, number][]) {
    for (let t = 0.02; t <= 1.0001; t += 0.02) {
      const hex = toHex(mix(base, target, t))
      const score = worst(hex)
      if (score >= TEXT_CONTRAST_FLOOR) { candidates.push(hex); break }
      if (score > bestScore) { best = hex; bestScore = score }
    }
  }
  if (!candidates.length) return best
  return candidates.sort((a, b) =>
    (perceptualDistance(muted, a) - perceptualDistance(muted, b)) || a.localeCompare(b))[0]!
}

/** The resolved material for a brand — its own overrides win over the derivation. */
export function material(t: BrandTokens): Material {
  const b = rgb(t.colors.brand)
  const a = rgb(t.colors.accent)
  const ink = rgb(t.colors.ink)
  const derived: Material = {
    // LIGHT — three very large, very low-contrast pools over the flat bg. A white
    // pool top-left (the light source), the brand mid-right, the accent below the
    // fold, so scrolling moves through the stone rather than across a flat sheet.
    baseLight: [
      `radial-gradient(120% 80% at 12% 0%, rgba(255,255,255,.95) 0%, rgba(255,255,255,0) 58%)`,
      `radial-gradient(90% 70% at 92% 14%, rgba(${b},.10) 0%, rgba(${b},0) 60%)`,
      `radial-gradient(120% 90% at 50% 108%, rgba(${a},.09) 0%, rgba(${a},0) 62%)`,
      t.colors.bg,
    ].join(','),
    // DARK — the light source moves behind the stone: the brand glows at the top
    // edge instead of a white pool sitting on it.
    baseDark: [
      `radial-gradient(110% 75% at 14% -4%, rgba(${b},.16) 0%, rgba(${b},0) 58%)`,
      `radial-gradient(95% 70% at 88% 8%, rgba(${a},.12) 0%, rgba(${a},0) 62%)`,
      `radial-gradient(140% 100% at 50% 110%, rgba(${b},.06) 0%, rgba(${b},0) 60%)`,
      t.colors.bgDark,
    ].join(','),
    rimLight: 'rgba(255,255,255,.9)',
    rimDark: 'rgba(255,255,255,.07)',
    // The ramp is one shape at three depths, so elevation reads as distance and
    // not as three unrelated shadows.
    e1Light: `0 1px 2px rgba(${ink},.04), 0 18px 40px -22px rgba(${ink},.28), 0 2px 10px -6px rgba(${ink},.06)`,
    e2Light: `0 2px 4px rgba(${ink},.05), 0 28px 60px -28px rgba(${ink},.34), 0 4px 16px -8px rgba(${ink},.08)`,
    e3Light: `0 4px 8px rgba(${ink},.06), 0 48px 96px -36px rgba(${ink},.42), 0 8px 28px -12px rgba(${ink},.10)`,
    e1Dark: `0 20px 44px -26px rgba(0,0,0,.85), 0 0 0 1px rgba(255,255,255,.035)`,
    e2Dark: `0 28px 64px -30px rgba(0,0,0,.9), 0 0 0 1px rgba(255,255,255,.05)`,
    e3Dark: `0 48px 104px -40px rgba(0,0,0,.95), 0 0 0 1px rgba(255,255,255,.07)`,
    relAccents: relAccents(t.colors.brand),
  }
  return { ...derived, ...(t.material ?? {}) }
}

/**
 * The ONE definition of what a brand emits — every custom property, for a given
 * resolved mode. `tokensToCss` (static render) and the console's ThemeProvider
 * (runtime `style.setProperty`) both read this, so the two paths cannot drift.
 * They were separate implementations of the same list, which is exactly the
 * seam that ships a variable defined on one path and missing on the other.
 */
export function cssVars(t: BrandTokens, mode: 'light' | 'dark'): Record<string, string> {
  const c = t.colors
  const m = material(t)
  const dark = mode === 'dark'
  const bg = dark ? c.bgDark : c.bg
  const surface = dark ? c.surfaceDark : c.surface
  return {
    '--brand': c.brand, '--brand-contrast': c.brandContrast, '--accent': c.accent,
    // Persistent customization chrome is intentionally inverse in BOTH modes.
    // Derive it from each brand's own dark palette so a white-label host does
    // not inherit Moonbase colour, and emit it through the same static/runtime
    // seam as every other governed brand value (#1991).
    '--mode-bar-bg': c.bgDark, '--mode-bar-fg': c.inkDark,
    // The SECONDARY foreground on that same inverse ground (#2347 §2).
    //
    // The inverse pair is a primary foreground and nothing else, so a header
    // that wants a quieter second line — "by", a summary, a caption — has had
    // to invent one, and the two ways to invent one are both defects: an
    // opacity on the primary, which is what stops passing contrast on a dark
    // ground, or a literal grey, which is the second hand-tuned dark palette §2
    // forbids by name.
    //
    // DERIVED, therefore, through `mutedText` — the same clamp `--muted` itself
    // goes through, argued once — starting from the brand's OWN dark muted ink
    // and measured against the inverse background, which is the one ground it
    // can occupy. Mode-independent for the reason the pair above is: a contrast
    // header does not flip when the page does, and `header-treatment.test.ts`
    // asserts that alongside the 4.5:1 floor for every registered brand.
    //
    // There is deliberately no `--mode-bar-hair` beside it: a separator is
    // non-text and `CustomizationModeBar.module.css` already derives one in CSS
    // from the foreground it is given (`color-mix(… 34%, transparent)`). A
    // fourth derivation here would be a second baseline for a property one
    // already covers (rule 42).
    '--mode-bar-muted': mutedText(c.mutedDark, [c.bgDark]),
    // The keyboard focus indicator. DERIVED, never the raw `--brand`: the ring
    // is a WCAG 1.4.11 conformance surface and `--brand` is customer-supplied,
    // so drawing one with the other let a customer make every keyboard
    // affordance in the product invisible by choosing their own colour
    // (COCCOLARE dark measured 1.71:1). It keeps the brand where the brand
    // already clears 3:1. See `focusRing` above and
    // `packages/ui/src/theme/focus-ring.test.ts`.
    '--focus-ring': focusRing(c.brand, [bg, surface]),
    // Brand-coloured TEXT. DERIVED for the same reason `--focus-ring` is, one
    // floor higher (SC 1.4.3, 4.5:1): `color: var(--brand)` let a customer's own
    // colour decide whether the product's labels, links, active tabs and figures
    // were readable, and in six of the eight registered brand x mode cells it
    // decided NO — COCCOLARE dark measured 1.37:1. Clamped against every ground
    // brand text actually lands on, tints included. See `brandText` above and
    // `packages/ui/src/theme/brand-text-contrast.test.ts`.
    '--brand-text': brandText(c.brand, Object.values(brandTextGrounds(t, mode))),
    '--brand-gradient': t.gradient ?? c.brand,
    '--bg': bg,
    '--surface': surface,
    '--ink': dark ? c.inkDark : c.ink,
    // The universal muted ink, DERIVED (#2129). It keeps its name, its role and
    // every one of its 755 text call sites; only the value moves, and only where
    // the author's own value does not already clear 4.5:1 on every ground it can
    // occupy — flat surface, marble pool, and ONE conservative quiet tint over
    // each (`QUIET_TINT_STACK`, which is 1: stacking the 14% bound twice invents
    // a ground the browser never paints and makes five dark cells unsatisfiable).
    // ALL EIGHT registered brand x mode cells failed on the raw value, worst
    // COCCOLARE light at 2.61:1 on a quiet tint over a marble pool — the 3.23:1
    // this comment used to quote is that pool UNTINTED, which is not the binding
    // ground. See `mutedText` above and
    // `packages/ui/src/theme/muted-text-contrast.test.ts`.
    '--muted': mutedText(dark ? c.mutedDark : c.muted,
      Object.values(mutedTextGrounds(t, mode).grounds)),
    '--border': dark ? c.borderDark : c.border,
    '--font-sans': t.font.sans, '--font-mono': t.font.mono,
    '--radius': t.radius,
    // the material
    '--base': dark ? m.baseDark : m.baseLight,
    '--rim': dark ? m.rimDark : m.rimLight,
    '--e1': dark ? m.e1Dark : m.e1Light,
    '--e2': dark ? m.e2Dark : m.e2Light,
    '--e3': dark ? m.e3Dark : m.e3Light,
    // `--shadow` predates the ramp and is used in ~30 modules; it stays, as e1.
    // `--shadow-lg` was used in comms and feedback and NEVER defined — those
    // popovers have been rendering with no shadow at all. It is e2.
    '--shadow': t.shadow, '--shadow-lg': dark ? m.e2Dark : m.e2Light,
    // relationship depth (#2019) — one set for both modes; these identify DEPTH,
    // not state, and a light/dark pair here would be two palettes to drift.
    '--rel-1': m.relAccents[0], '--rel-2': m.relAccents[1],
    '--rel-3': m.relAccents[2], '--rel-4': m.relAccents[3],
  }
}

/** Emit the token set as CSS custom properties (light values + a dark block). */
export function tokensToCss(t: BrandTokens): string {
  const decl = (mode: 'light' | 'dark') =>
    Object.entries(cssVars(t, mode)).map(([k, v]) => `  ${k}:${v};`).join('\n')
  // The dark block repeats every property rather than only the ones that change:
  // a partial override is how a value that STOPS differing silently keeps its
  // light value in dark mode.
  return `:root{\n${decl('light')}\n}\n:root[data-theme="dark"]{\n${decl('dark')}\n}`
}
